'use client';

import React, { useEffect, useState } from 'react';
import { useGetDataIssueEmailSuggestionsQuery as useGetDataIssueEmailSuggestions } from 'src/api/redux';
import { Chip, CircularProgress, PulseRadio } from 'src/components/PulseUI';
import { DataIssueEmail } from './DataIssueEmail';
import { DataIssueTypes } from './types';

interface SuggestionItemProps {
  item: {
    email: string;
    suggestionType: string;
    confidence?: number;
    is_primary?: boolean;
  };
  isSelected: boolean;
  onSelect: (value: string) => void;
}

const SuggestionItem = ({ item, isSelected, onSelect }: SuggestionItemProps) => {
  const testId =
    item.suggestionType === 'email'
      ? `email-suggestion-${item.email}`
      : 'email-suggestion-other';

  return (
    <li
      className={`relative flex items-center gap-3 rounded-lg border p-4 shadow-sm cursor-pointer transition-colors ${
        isSelected
          ? 'border-2 border-sky-500'
          : 'border border-neutral-200 hover:border-neutral-300'
      }`}
      onClick={() => onSelect(item.email)}
      data-testid={testId}
    >
      <div className="shrink-0">
        <PulseRadio
          label=""
          name="email-suggestion"
          value={item.email}
          checked={isSelected}
          onChange={() => onSelect(item.email)}
          aria-label={
            item.suggestionType === 'email'
              ? `Select email ${item.email}`
              : 'Select other option'
          }
          data-testid={`${testId}-radio`}
        />
      </div>
      {item.suggestionType === 'email' ? (
        <EmailSuggestion emailObject={item} />
      ) : (
        <OtherSuggestion />
      )}
    </li>
  );
};

interface EmailSuggestionProps {
  emailObject: {
    email: string;
    confidence?: number;
    is_primary?: boolean;
  };
}

const EmailSuggestion = ({ emailObject }: EmailSuggestionProps) => {
  return (
    <div className="flex flex-1 items-center justify-between">
      <div className="flex flex-col gap-0.5">
        <label className="text-sm font-medium text-gray-700">{emailObject?.email}</label>
        {emailObject?.confidence && (
          <span className="text-xs text-neutral-500">
            Confidence: {emailObject?.confidence}%
          </span>
        )}
      </div>
      {emailObject?.is_primary && (
        <Chip variant="soft" color="primary" size="sm">
          Current Email
        </Chip>
      )}
    </div>
  );
};

const OtherSuggestion = () => {
  return (
    <div className="flex flex-col gap-0.5">
      <label className="text-sm font-medium text-gray-700">Other</label>
      <span className="text-xs text-neutral-500">Please specify below</span>
    </div>
  );
};

interface DataIssueEmailSuggestionsProps {
  email: string;
  setEmail: (value: string) => void;
  emailId: string;
  setEmailId: (value: string) => void;
  issueType: DataIssueTypes;
  providerId: string;
  providerType: string;
}

const DataIssueEmailSuggestions = ({
  email,
  setEmail,
  emailId,
  setEmailId,
  issueType,
  providerId,
  providerType,
}: DataIssueEmailSuggestionsProps): React.ReactElement => {
  const [userSuppliedEmail, setUserSuppliedEmail] = useState(
    emailId === 'other' ? email : ''
  );
  const { data, isLoading } = useGetDataIssueEmailSuggestions({
    providerId,
    providerType,
    issueType,
  });

  const suggestedEmails = data?.results;

  useEffect(() => {
    if (emailId) {
      setEmail(emailId);
      setEmailId(emailId);
    } else if (suggestedEmails?.length > 0) {
      setEmail(suggestedEmails?.[0]?.email);
      setEmailId(suggestedEmails?.[0]?.email);
    } else {
      setEmail('');
      setEmailId('other');
    }
  }, [suggestedEmails, emailId, setEmailId, setEmail]);

  useEffect(() => {
    if (userSuppliedEmail) {
      setEmail(userSuppliedEmail);
    }

    if (emailId === 'other') {
      setEmail(userSuppliedEmail);
    }
  }, [userSuppliedEmail, setEmail, emailId]);

  const handleChange = (selectedRadioValue: string) => {
    setEmailId(selectedRadioValue);

    if (selectedRadioValue === 'other') {
      setEmail(userSuppliedEmail);
    } else {
      setEmail(selectedRadioValue);
      setUserSuppliedEmail('');
    }
  };

  if (isLoading) {
    return (
      <div
        className="flex h-full items-center justify-center pt-4"
        data-testid="data-issue-email-suggestions-loading"
      >
        <CircularProgress data-testid="circular-progress" />
      </div>
    );
  }

  return (
    <div data-testid="data-issue-email-suggestions">
      <div className="text-sm font-medium">Select or enter a correction:</div>
      <ul
        role="radiogroup"
        aria-label="Email suggestions"
        className="flex min-w-60 flex-col gap-2"
        data-testid="email-suggestions-radiogroup"
      >
        {suggestedEmails?.map((item, index) => (
          <SuggestionItem
            key={index}
            item={{ ...item, suggestionType: issueType }}
            isSelected={emailId === item.email}
            onSelect={handleChange}
          />
        ))}
        <SuggestionItem
          item={{ email: 'other', suggestionType: 'other' }}
          isSelected={emailId === 'other'}
          onSelect={handleChange}
        />
      </ul>
      {emailId === 'other' && (
        <DataIssueEmail
          email={userSuppliedEmail}
          setEmail={setUserSuppliedEmail}
        />
      )}
    </div>
  );
};

export { DataIssueEmailSuggestions };
